/**
 * Contextualização de multimídia (imagens e vídeos) do conteúdo das matérias.
 *
 * Garante que toda imagem tenha um ALT descritivo e todo vídeo/embed tenha um
 * title descritivo, já que o Google (e outras IAs) usam esses atributos para
 * entender e reaproveitar a multimídia nas respostas geradas.
 *
 * Trabalha sobre strings para funcionar no SSR (sem depender do DOM).
 */

type ImageLike = {
    alternative?: string
    legend?: string
    title?: string
}

// Títulos genéricos que não descrevem o conteúdo e devem ser substituídos
const GENERIC_MEDIA_TITLES = new Set([
    'youtube video player',
    'youtube',
    'iframe',
    'video',
    'vídeo',
    'embed',
    'spotify embed'
])

const MAX_ALT_LENGTH = 250

// Entidades nomeadas mais comuns no conteúdo do WordPress (wptexturize)
const NAMED_ENTITIES: Record<string, string> = {
    amp: '&',
    quot: '"',
    apos: "'",
    lt: '<',
    gt: '>',
    nbsp: ' ',
    hellip: '…',
    ndash: '–',
    mdash: '—',
    lsquo: '‘',
    rsquo: '’',
    ldquo: '“',
    rdquo: '”',
    laquo: '«',
    raquo: '»'
}

/**
 * Decodifica entidades HTML (nomeadas e numéricas) para texto simples.
 * Funciona no SSR, diferente do decodeHtmlEntities de geral.ts, que depende do DOM.
 * @param {string} text
 * @return {string}
 */
export function decodeEntities(text: string): string {
    return text.replace(
        /&(#x[0-9a-f]+|#\d+|[a-z]+);/gi,
        (entity, code: string) => {
            if (code.startsWith('#')) {
                const isHex = code[1].toLowerCase() === 'x'
                const point = Number.parseInt(
                    code.slice(isHex ? 2 : 1),
                    isHex ? 16 : 10
                )
                try {
                    return String.fromCodePoint(point)
                } catch {
                    return entity
                }
            }
            return NAMED_ENTITIES[code.toLowerCase()] ?? entity
        }
    )
}

/**
 * Converte HTML em texto simples: remove tags, decodifica entidades e
 * normaliza espaços. Valores que não são string (ex: `false` que o WordPress
 * devolve em campos vazios) são tratados como texto vazio.
 * @param {unknown} html
 * @return {string}
 */
export function stripTags(html: unknown): string {
    if (typeof html !== 'string') return ''
    return decodeEntities(html.replace(/<[^<>]*>/g, ' '))
        .replace(/\s+/g, ' ')
        .trim()
}

/**
 * Escapa texto simples para uso seguro dentro de um atributo HTML.
 * @param {string} value
 * @return {string}
 */
function escapeAttribute(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
}

/**
 * Limita o tamanho do texto alternativo sem cortar palavras.
 * @param {string} text
 * @return {string}
 */
function limitLength(text: string): string {
    if (text.length <= MAX_ALT_LENGTH) return text
    const cut = text.slice(0, MAX_ALT_LENGTH)
    return `${cut.slice(0, cut.lastIndexOf(' ')).trim()}…`
}

/**
 * Lê o valor de um atributo de uma tag HTML.
 * @param {string} tag
 * @param {string} attribute
 * @return {string|undefined}
 */
function getAttribute(tag: string, attribute: string): string | undefined {
    const match = new RegExp(
        String.raw`\s${attribute}\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))`,
        'i'
    ).exec(tag)
    if (!match) return undefined
    return match[1] ?? match[2] ?? match[3]
}

/**
 * Define (ou substitui) um atributo em uma tag HTML de abertura.
 * @param {string} tag
 * @param {string} attribute
 * @param {string} value
 * @return {string}
 */
function setAttribute(tag: string, attribute: string, value: string): string {
    const withoutAttribute = tag.replace(
        new RegExp(
            String.raw`\s${attribute}\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)|\s${attribute}(?=[\s/>])`,
            'i'
        ),
        ''
    )
    return withoutAttribute.replace(
        /^<(\w+)/,
        `<$1 ${attribute}="${escapeAttribute(value)}"`
    )
}

/**
 * Indica se o texto existente já descreve a mídia.
 * @param {string|undefined} value
 * @return {boolean}
 */
function isDescriptive(value?: string): boolean {
    const normalized = stripTags(value ?? '').toLowerCase()
    return normalized.length > 0 && !GENERIC_MEDIA_TITLES.has(normalized)
}

/**
 * Indica se a imagem foi marcada como decorativa e deve manter o alt vazio.
 * O alt="" sozinho não basta: o WordPress grava alt="" em toda imagem sem
 * texto alternativo preenchido.
 * @param {string} tag
 * @return {boolean}
 */
function isDecorative(tag: string): boolean {
    const role = getAttribute(tag, 'role')?.toLowerCase()
    return (
        getAttribute(tag, 'alt') !== undefined &&
        (role === 'presentation' ||
            role === 'none' ||
            getAttribute(tag, 'aria-hidden')?.toLowerCase() === 'true')
    )
}

/**
 * Resolve o ALT da imagem destacada da matéria.
 * Ordem: texto alternativo > legenda > título da imagem > contexto (título da matéria).
 * @param {ImageLike|undefined} image
 * @param {string} context - Normalmente o título da matéria
 * @return {string}
 */
export function resolveImageAlt(
    image: ImageLike | undefined,
    context = ''
): string {
    const candidates = [image?.alternative, image?.legend, image?.title]
    const alt = candidates.map((c) => stripTags(c)).find(Boolean)
    return limitLength(alt || stripTags(context))
}

/**
 * Gera um título descritivo para um vídeo/embed com base na origem.
 * @param {string} src
 * @param {string} context - Normalmente o título da matéria
 * @return {string}
 */
export function resolveMediaTitle(src = '', context = '') {
    const description = stripTags(context)
    const prefix = (() => {
        if (/youtube\.com|youtu\.be|vimeo\.com/i.test(src)) return 'Vídeo'
        if (/spotify\.com|soundcloud\.com|anchor\.fm/i.test(src))
            return 'Podcast'
        if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(src)) return 'Vídeo'
        return 'Conteúdo incorporado'
    })()
    return description ? `${prefix}: ${description}` : prefix
}

/**
 * Garante o ALT em uma tag <img>.
 * @param {string} tag
 * @param {string} fallback
 * @return {string}
 */
function ensureImageAlt(tag: string, fallback: string): string {
    if (isDescriptive(getAttribute(tag, 'alt')) || isDecorative(tag)) return tag
    const alt = limitLength(
        stripTags(getAttribute(tag, 'title') ?? '') || stripTags(fallback)
    )
    return alt ? setAttribute(tag, 'alt', alt) : tag
}

/**
 * Garante o title em uma tag <iframe> ou <video>.
 * @param {string} tag
 * @param {string} context
 * @return {string}
 */
function ensureMediaTitle(tag: string, context: string): string {
    if (isDescriptive(getAttribute(tag, 'title'))) return tag
    const src = getAttribute(tag, 'src') ?? ''
    const title = /^<video\b/i.test(tag)
        ? resolveMediaTitle(src || 'video.mp4', context)
        : resolveMediaTitle(src, context)
    return setAttribute(tag, 'title', title)
}

/**
 * Contextualiza a multimídia do HTML da matéria:
 * - <img> sem ALT recebe a legenda da <figure> (figcaption), o title da imagem
 *   ou, por último, o título da matéria;
 * - <iframe>/<video> sem title descritivo recebe um title com o tipo da mídia
 *   e o título da matéria.
 *
 * @param {string} html - Conteúdo HTML da matéria
 * @param {string} context - Título da matéria, usado como contexto de fallback
 * @return {string}
 */
export function contextualizeContentMedia(html = '', context = ''): string {
    if (typeof html !== 'string' || !html) return html

    // Imagens dentro de <figure> usam a legenda como descrição
    let result = html.replace(/<figure\b[\s\S]*?<\/figure>/gi, (figure) => {
        const caption = /<figcaption\b[^>]*>([\s\S]*?)<\/figcaption>/i.exec(
            figure
        )
        const fallback = stripTags(caption?.[1]) || context
        return figure.replace(/<img\b[^>]*>/gi, (img) =>
            ensureImageAlt(img, fallback)
        )
    })

    result = result.replace(/<img\b[^>]*>/gi, (img) =>
        ensureImageAlt(img, context)
    )

    result = result.replace(/<(?:iframe|video)\b[^>]*>/gi, (tag) =>
        ensureMediaTitle(tag, context)
    )

    return result
}
